import { useProductsWithInfoElectronico } from '@/data/Content/_Products_InfoElectronico';
import { dataMapMerchandisingAssociation } from '@/data/Content/MerchandisingAssociation';
import { ProductType } from '@/data/types/Product';
import { useMemo } from 'react';
import { useSingleSettings } from '@/data/Settings';
import getValueByPathFromUnknown from '../utils/getValueByPathFromUnknown';

export const MERCHANDISING_ASSOCIATION_TYPE_COLOR = 'COLOR';

export const useProductExtractMerchandisingAssociationsEnabled = () => {
	const { settings: singleSettings } = useSingleSettings();

	const availableFeatured = useMemo(() => {
		const userData = singleSettings?.userData || {};
		const disabledForOffers = getValueByPathFromUnknown(userData, ['association_colors_enabled']);
		return disabledForOffers === 'true' || disabledForOffers === true || disabledForOffers === '1';
	}, [singleSettings?.userData]);

	return { availableFeatured };
};

export const useProductExtractMerchandisingAssociations = (product?: ProductType) => {
	const { availableFeatured } = useProductExtractMerchandisingAssociationsEnabled();

	const productVariantColors = useMemo<ProductType[]>(() => {
		if (!product || !availableFeatured) {
			return [];
		}

		const associations = dataMapMerchandisingAssociation(product);

		if (!associations?.length) {
			return [];
		}

		let productColors = associations.filter(
			(association) => association.associationType === MERCHANDISING_ASSOCIATION_TYPE_COLOR
		);

		const hasParent = productColors.some(
			(association) => association.partNumber === product.partNumber
		);

		if (productColors.length > 0 && !hasParent) {
			productColors.unshift({
				...product,
				associationType: MERCHANDISING_ASSOCIATION_TYPE_COLOR,
			});

			productColors = productColors.sort((a, b) => {
				const aIsParent = a.name === product.name;
				const bIsParent = b.name === product.name;
				if (aIsParent) return -1;
				if (bIsParent) return 1;
				return 0;
			});
		}

		return productColors;
	}, [product, availableFeatured]);

	return { productVariantColors };
};

export const useProductExtractMerchandisingAssociationsWithInfoElectronico = (
	product?: ProductType
) => {
	const { productVariantColors: rawProductVariantColors } =
		useProductExtractMerchandisingAssociations(product);

	const { data, isLoading } = useProductsWithInfoElectronico(rawProductVariantColors);

	const { data: currentProducts, isLoading: isLoadingCurrent } = useProductsWithInfoElectronico(
		product ? [product] : []
	);

	const currentProduct = useMemo(
		() => currentProducts?.[0] ?? product,
		[currentProducts, product]
	);

	return { product: currentProduct, data, isLoading: isLoading || isLoadingCurrent };
};
